import { useMemo, useState } from "react"
import { useParams } from "react-router"
import { CheckCircle, Phone01, CalendarDate } from "@untitledui/icons"
import {
  useBookingInfo,
  useBookingSlots,
  useConfirmBooking,
  useCancelBooking,
} from "@/lib/api/use-appointments"
import type { AppointmentKind } from "@/lib/api/appointments"
import { cx } from "@/lib/utils/cx"

// The customer's side of the follow-up link — public, no login, one job:
// pick a time in a few taps. The same link reschedules or cancels later.
// White-label: it's the shop's page, top to bottom.

function dayKeyOf(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
}

export default function BookPage() {
  const { token } = useParams()
  const info = useBookingInfo(token ?? null)
  const from = useMemo(() => new Date().toISOString(), [])
  const to = useMemo(() => new Date(Date.now() + 14 * 86_400_000).toISOString(), [])
  const slotsQ = useBookingSlots(token ?? null, from, to)
  const confirm = useConfirmBooking(token ?? null)
  const cancelBooking = useCancelBooking(token ?? null)

  const [kind, setKind] = useState<AppointmentKind>("dropoff")
  const [dayIdx, setDayIdx] = useState(0)
  const [picked, setPicked] = useState<string | null>(null)
  const [justBooked, setJustBooked] = useState(false)
  const [rescheduling, setRescheduling] = useState(false)

  const days = useMemo(() => {
    const map = new Map<string, { date: Date; slots: string[] }>()
    for (const s of slotsQ.data ?? []) {
      const k = dayKeyOf(s.startsAt)
      const entry = map.get(k) ?? { date: new Date(s.startsAt), slots: [] }
      entry.slots.push(s.startsAt)
      map.set(k, entry)
    }
    return [...map.values()].sort((a, b) => a.date.getTime() - b.date.getTime()).slice(0, 8)
  }, [slotsQ.data])

  const d = info.data
  const firstName = d?.customerName?.split(" ")[0] ?? "there"

  const fmtWhen = (iso: string) =>
    new Date(iso).toLocaleString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })

  const doConfirm = () => {
    if (!picked || confirm.isPending) return
    confirm.mutate(
      { startsAt: picked, kind },
      {
        onSuccess: () => {
          setJustBooked(true)
          setRescheduling(false)
          setPicked(null)
          info.refetch()
        },
      },
    )
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      <div className="mx-auto max-w-md px-4 py-8">
        {/* The shop's masthead — their page, not ours. */}
        {d && (
          <div className="flex items-center gap-3 mb-6">
            <div className="size-11 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 grid place-items-center text-white text-[15px] font-bold shadow-md shadow-emerald-600/25">
              {d.shop.initials}
            </div>
            <div className="min-w-0">
              <p className="text-[15px] font-bold text-zinc-900 truncate">{d.shop.name}</p>
              {d.shop.phone && (
                <a
                  href={`tel:${d.shop.phone.replace(/[^\d+]/g, "")}`}
                  className="inline-flex items-center gap-1 text-[12px] text-zinc-500"
                >
                  <Phone01 className="size-3" aria-hidden />
                  {d.shop.phone}
                </a>
              )}
            </div>
          </div>
        )}

        {info.isLoading ? (
          <div className="h-72 rounded-2xl bg-white border border-zinc-200/70 animate-pulse" />
        ) : !d ? (
          <Card>
            <p className="text-[15px] font-bold text-zinc-900">This link isn't valid</p>
            <p className="text-[13px] text-zinc-500 mt-1">
              It may have been replaced — call the shop and they'll get you scheduled.
            </p>
          </Card>
        ) : d.state === "expired" ? (
          <Card>
            <p className="text-[15px] font-bold text-zinc-900">This link has expired</p>
            <p className="text-[13px] text-zinc-500 mt-1">
              No problem — give {d.shop.name} a call and they'll get you scheduled.
            </p>
          </Card>
        ) : (d.state === "booked" || justBooked) && !rescheduling ? (
          <Card>
            <div className="size-12 rounded-2xl bg-emerald-50 grid place-items-center mb-3">
              <CheckCircle className="size-6 text-emerald-600" aria-hidden />
            </div>
            <p className="text-[17px] font-bold text-zinc-900">
              {justBooked ? "You're booked!" : "You're on the calendar"}
            </p>
            {d.appointment && (
              <p className="text-[14px] text-zinc-700 mt-1.5">
                {fmtWhen(d.appointment.startsAt)}
                <span className="text-zinc-400">
                  {" "}
                  · {d.appointment.kind === "inspection" ? "quick look" : "drop-off"}
                </span>
              </p>
            )}
            <p className="text-[12.5px] text-zinc-500 mt-1">
              {d.vehicleLabel ?? "Your vehicle"} — see you then, {firstName}.
            </p>
            <div className="mt-5 flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  setRescheduling(true)
                  setJustBooked(false)
                }}
                className="h-10 px-4 rounded-xl border border-zinc-200 bg-white text-[13px] font-semibold text-zinc-700 hover:border-zinc-300"
              >
                Change time
              </button>
              <button
                type="button"
                disabled={cancelBooking.isPending}
                onClick={() =>
                  cancelBooking.mutate(undefined, { onSuccess: () => info.refetch() })
                }
                className="h-10 px-4 rounded-xl text-[13px] font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
              >
                {cancelBooking.isPending ? "Cancelling…" : "Cancel"}
              </button>
            </div>
          </Card>
        ) : (
          <>
            <Card>
              <p className="text-[19px] font-bold text-zinc-900 leading-snug">
                Hi {firstName} — let's get your{" "}
                <span className="whitespace-nowrap">{d.vehicleLabel ?? "vehicle"}</span> in.
              </p>
              <p className="text-[13px] text-zinc-500 mt-1">
                {d.estimateValue != null && (
                  <>
                    Your estimate:{" "}
                    <span className="font-semibold text-zinc-700 tabular-nums">
                      ${d.estimateValue.toLocaleString()}
                    </span>
                    {" · "}
                  </>
                )}
                Pick any time that works — it takes one tap.
              </p>

              {d.options.inspectionEnabled && d.options.dropoffEnabled && (
                <div className="mt-4 flex gap-1.5">
                  <KindButton
                    active={kind === "dropoff"}
                    onClick={() => setKind("dropoff")}
                    title="Drop it off"
                    sub="leave it with us"
                  />
                  <KindButton
                    active={kind === "inspection"}
                    onClick={() => setKind("inspection")}
                    title="Quick look"
                    sub={`about ${d.options.slotMinutes} min`}
                  />
                </div>
              )}

              {/* Day pills */}
              <div className="mt-4 flex gap-1.5 overflow-x-auto pb-1">
                {days.map((day, i) => (
                  <button
                    key={day.date.toISOString()}
                    type="button"
                    onClick={() => {
                      setDayIdx(i)
                      setPicked(null)
                    }}
                    className={cx(
                      "shrink-0 rounded-xl px-3 py-2 text-center",
                      i === dayIdx
                        ? "bg-zinc-900 text-white"
                        : "bg-white border border-zinc-200 text-zinc-700",
                    )}
                  >
                    <span className="block text-[10.5px] font-semibold uppercase opacity-70">
                      {day.date.toLocaleDateString("en-US", { weekday: "short" })}
                    </span>
                    <span className="block text-[14px] font-bold tabular-nums">
                      {day.date.getDate()}
                    </span>
                  </button>
                ))}
              </div>

              {/* Slots for the picked day */}
              <div className="mt-3 grid grid-cols-3 gap-1.5">
                {(days[dayIdx]?.slots ?? []).map((iso) => (
                  <button
                    key={iso}
                    type="button"
                    onClick={() => setPicked(iso)}
                    className={cx(
                      "h-10 rounded-xl text-[13px] font-semibold tabular-nums transition-colors",
                      picked === iso
                        ? "bg-emerald-600 text-white"
                        : "bg-white border border-zinc-200 text-zinc-700 hover:border-emerald-300",
                    )}
                  >
                    {new Date(iso).toLocaleTimeString("en-US", {
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </button>
                ))}
                {days.length > 0 && (days[dayIdx]?.slots ?? []).length === 0 && (
                  <p className="col-span-3 text-center text-[12.5px] text-zinc-400 py-4">
                    Nothing open this day — try another.
                  </p>
                )}
              </div>

              {confirm.isError && (
                <p className="mt-3 text-[12.5px] text-red-600">
                  That time just filled up — pick another.
                </p>
              )}

              <button
                type="button"
                disabled={!picked || confirm.isPending}
                onClick={doConfirm}
                className="mt-4 w-full h-12 rounded-xl bg-emerald-600 text-white text-[15px] font-semibold hover:bg-emerald-700 transition-colors disabled:opacity-40 inline-flex items-center justify-center gap-2"
              >
                <CalendarDate className="size-4" aria-hidden />
                {confirm.isPending
                  ? "Booking…"
                  : picked
                    ? `Book ${new Date(picked).toLocaleString("en-US", {
                        weekday: "short",
                        hour: "numeric",
                        minute: "2-digit",
                      })}`
                    : "Pick a time above"}
              </button>
              {rescheduling && (
                <button
                  type="button"
                  onClick={() => setRescheduling(false)}
                  className="mt-2 w-full h-10 rounded-xl text-[13px] font-semibold text-zinc-500 hover:bg-zinc-100"
                >
                  Keep my current time
                </button>
              )}
            </Card>
            <p className="text-center text-[11.5px] text-zinc-400 mt-4">
              Booking with {d.shop.name} — no account needed.
            </p>
          </>
        )}
      </div>
    </div>
  )
}

function Card({ children }: { children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-zinc-200/80 bg-white shadow-sm p-5">{children}</div>
  )
}

function KindButton({
  active,
  onClick,
  title,
  sub,
}: {
  active: boolean
  onClick: () => void
  title: string
  sub: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        "flex-1 rounded-xl px-3 py-2 text-left border transition-colors",
        active
          ? "border-emerald-500 bg-emerald-50/60"
          : "border-zinc-200 bg-white hover:border-zinc-300",
      )}
    >
      <span
        className={cx(
          "block text-[13px] font-bold",
          active ? "text-emerald-800" : "text-zinc-800",
        )}
      >
        {title}
      </span>
      <span className="block text-[11px] text-zinc-500">{sub}</span>
    </button>
  )
}
